import { useState } from 'react';
import { useIntl } from 'react-intl';
import { ChevronDown, Check } from 'lucide-react';
import { foundation } from '../../ui/tokens';
import { components, semantic } from '../../ui/theme';
import { alphaOf } from '../../utils/ui/colorAlpha';
import Skeleton from './Skeleton';
import PoolTypeTag from './PoolTypeTag';
import { getInitials } from '../../utils/format/initials';
import { formatPersonName } from '../../utils/format/formatPersonName';
import { semantic as sem } from '../../ui/theme/semanticTokens';

const fc = foundation.color;
const sp = foundation.spacePx;
const fs = foundation.fontSize;
const fw = foundation.fontWeight;
const rd = foundation.radii;

function WorkerAvatar({ initials, selected }) {
  return (
    <span
      style={{
        width: 20,
        height: 20,
        borderRadius: foundation.radii.full,
        flexShrink: 0,
        backgroundColor: selected ? fc.brand.primary : fc.brand.primaryMuted,
        color: selected ? fc.brand.onPrimary : fc.brand.text,
        fontSize: '9px',
        fontWeight: fw.bold,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {initials}
    </span>
  );
}

/**
 * Builds the attribution options: the signed-in user plus the customer's
 * assigned worker (when distinct). Admin and supervisor share this branch —
 * neither is offered the full tenant roster.
 */
function buildOptions({ role, sessionUser, customer, intl }) {
  if (!role || role === 'worker' || !sessionUser?.id) return [];
  const selfName = sessionUser.user_metadata?.full_name
    || sessionUser.email
    || intl.formatMessage({ id: 'common.me', defaultMessage: 'Me' });
  // Admin + supervisor both attribute only to themselves or the customer's
  // assigned worker — never the full tenant roster. Exposing every worker was
  // misleading (it implied anyone could have performed the service) and served
  // no real workflow: coverage/substitution doesn't happen in practice. The
  // dropdown is UX only — the server validates the persisted worker_id.
  if (role === 'admin' || role === 'supervisor') {
    const opts = [{ id: sessionUser.id, full_name: selfName, isSelf: true, initialsSource: selfName }];
    // Synthesize the assigned worker from the customer record so the collapsed
    // trigger shows the right name on the first frame. `isPlaceholder` (id but
    // no resolved name yet) renders a loading skeleton instead of the ugly
    // `Worker (xxxx…)` stub, so the name never visibly churns.
    const assignedId = customer?.assigned_worker;
    if (assignedId && assignedId !== sessionUser.id) {
      const realName = customer?.assigned_worker_name;
      const assignedName = realName || `Worker (${assignedId.slice(0, 8)}…)`;
      opts.push({ id: assignedId, full_name: assignedName, initialsSource: assignedName, isPlaceholder: !realName });
    }
    return opts;
  }
  return [];
}

/**
 * RD-LOG-002 / RD-JOBS-004 — hero context pills for LogJob.
 *
 * When the role is admin or supervisor AND there's >1 attribution option,
 * renders an inline "Worker" dropdown picker (who the visit is credited to).
 * Otherwise nothing is shown for attribution (KAN-1064): a single option is
 * always the signed-in person, so a static "Logged by" line said nothing.
 */
export default function LogJobHeroContextPills({
  loggedByName,
  isFollowUp,
  isOffSeason,
  // Attribution dropdown props (optional — only admin / supervisor)
  role,
  sessionUser,
  customer,
  selectedRouteWorkerId,
  setSelectedRouteWorkerId,
  // True while the customer fetch is in flight. The optimistic nav-state seed
  // can lack the assignee join, so we withhold the name (skeleton) until the
  // fresh fetch lands — never flashing the signed-in admin's own name.
  attributionLoading,
  isPoolIndustry = false,
}) {
  const intl = useIntl();
  const [open, setOpen] = useState(false);

  const options = buildOptions({ role, sessionUser, customer, intl });
  // Admin / supervisor get the dropdown only when there's more than one
  // attribution option — i.e. the customer has an assigned worker distinct from
  // the signed-in user. With no assignee (or a self-assignment), `options` is
  // just [self] and the pill stays static. The synthetic assigned-worker option
  // (see buildOptions) is derived from the customer record already in hand, so
  // `options.length` reflects the final count on the first frame — the pill
  // paints in its final form (static vs dropdown) with no morph.
  const showDropdown =
    (role === 'admin' || role === 'supervisor')
    && Boolean(sessionUser?.id)
    && options.length > 1;

  // Mirror useLogJob's selectedRouteWorkerId initializer (assigned_worker || self)
  // so the displayed attribution is stable from the first frame — before that
  // init effect has run (it settles on the same value a tick later).
  const currentId = selectedRouteWorkerId || customer?.assigned_worker || sessionUser?.id;
  // Resolve WITHOUT falling back to options[0] (self) or loggedByName (the
  // signed-in admin). Those fallbacks are exactly what made the attribution
  // flash the admin's name and then snap to the worker. Until we have the real
  // attributee name — customer hydrated (`attributionLoading` false) AND a
  // matching, non-placeholder option — we render a loading skeleton instead.
  const currentOption = options.find((o) => o.id === currentId);
  const nameResolved = !attributionLoading && Boolean(currentOption) && !currentOption.isPlaceholder;
  const displayName = nameResolved ? currentOption.full_name : null;

  const defaultAssigned = customer?.assigned_worker;
  const showOverrideNote = showDropdown && defaultAssigned && currentId !== defaultAssigned;
  const assignedWorkerLabel = customer?.assigned_worker_name || null;

  if (!(loggedByName && showDropdown) && !isFollowUp && !isOffSeason) return null;

  return (
    <div style={rowStyle}>
      {loggedByName && (
        showDropdown ? (
          // ── In-hero dropdown trigger ──────────────────────────────────
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <button
              type="button"
              style={{ ...dropdownTriggerStyle, cursor: nameResolved ? 'pointer' : 'default' }}
              onClick={nameResolved ? () => setOpen((v) => !v) : undefined}
              disabled={!nameResolved}
              aria-haspopup="listbox"
              aria-expanded={open}
              aria-busy={!nameResolved}
              onMouseEnter={nameResolved ? (e) => { e.currentTarget.style.backgroundColor = fc.neutral.bgSubtle; } : undefined}
              onMouseLeave={nameResolved ? (e) => { e.currentTarget.style.backgroundColor = 'transparent'; } : undefined}
            >
              {nameResolved ? (
                <>
                  <span style={bylineLabel}>
                    {intl.formatMessage({ id: 'logJob.attribution.workerLabel', defaultMessage: 'Worker' })}
                  </span>
                  <WorkerAvatar initials={getInitials(currentOption.initialsSource || displayName)} selected={false} />
                  <span style={dropdownNameStyle}>
                    {displayName}
                    {currentOption.isSelf && (
                      <span style={selfTagStyle}>
                        {' '}{intl.formatMessage({ id: 'logJob.attribution.youTag', defaultMessage: '(you)' })}
                      </span>
                    )}
                  </span>
                </>
              ) : (
                // Whole-block loading affordance: skeleton the label, avatar AND
                // name together — mirroring the resolved [label][avatar][name]
                // layout so the avatar doesn't shift — so the pill reads as one
                // loading unit instead of "Logged by ▓▓▓".
                <>
                  <Skeleton width={52} height={10} borderRadius={999} />
                  <Skeleton width={20} height={20} borderRadius={rd.full} />
                  <Skeleton width={72} height={10} borderRadius={999} />
                </>
              )}
              <ChevronDown
                size={12}
                strokeWidth={2.25}
                aria-hidden
                style={{
                  transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 150ms ease',
                  opacity: nameResolved ? 0.55 : 0.3,
                  marginLeft: 1,
                }}
              />
            </button>

            {!nameResolved && showOverrideNote && (
              // Reserve the transfer-hint line as a skeleton too, so the whole
              // attribution block (pill + hint) loads as one unit and the layout
              // doesn't shift when the "Attributed to … instead of …" note lands.
              <Skeleton width={150} height={8} borderRadius={999} style={{ marginTop: 6 }} />
            )}

            {open && nameResolved && (
              <>
                <div
                  style={{ position: 'fixed', inset: 0, zIndex: 199 }}
                  onClick={() => setOpen(false)}
                />
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    marginTop: 6,
                    minWidth: 240,
                    backgroundColor: fc.neutral.bg,
                    border: `1px solid ${fc.neutral.border}`,
                    borderRadius: rd.lg,
                    boxShadow: '0 12px 32px -8px rgba(15, 23, 42, 0.20), 0 4px 12px -2px rgba(15, 23, 42, 0.08)',
                    padding: '6px',
                    zIndex: 200,
                    overflow: 'hidden',
                  }}
                  role="listbox"
                  aria-label={intl.formatMessage({ id: 'logJob.attribution.label', defaultMessage: 'Attribute to:' })}
                >
                  {options.map((opt) => {
                    const selected = opt.id === currentId;
                    return (
                      <button
                        key={opt.id}
                        type="button"
                        role="option"
                        aria-selected={selected}
                        style={{
                          ...dropdownItem,
                          backgroundColor: selected ? fc.neutral.bgSubtle : 'transparent',
                          fontWeight: selected ? fw.semibold : fw.medium,
                        }}
                        onClick={() => {
                          setSelectedRouteWorkerId(opt.id);
                          setOpen(false);
                        }}
                        onMouseEnter={selected ? undefined : (e) => { e.currentTarget.style.backgroundColor = fc.neutral.bgSubtle; }}
                        onMouseLeave={selected ? undefined : (e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                      >
                        <WorkerAvatar initials={getInitials(opt.initialsSource || opt.full_name)} selected={selected} />
                        <span style={{ fontSize: fs.sm, color: fc.neutral.text, flex: 1, textAlign: 'left' }}>
                          {formatPersonName(opt.full_name)}
                          {opt.isSelf && (
                            <span style={selfTagStyle}>
                              {' '}{intl.formatMessage({ id: 'logJob.attribution.youTag', defaultMessage: '(you)' })}
                            </span>
                          )}
                        </span>
                        {selected && <Check size={14} strokeWidth={2.5} aria-hidden style={{ color: fc.brand.text, flexShrink: 0 }} />}
                      </button>
                    );
                  })}
                </div>
              </>
            )}

            {showOverrideNote && nameResolved && assignedWorkerLabel && (
              <p style={noteStyle}>
                {intl.formatMessage(
                  { id: 'logJob.attribution.overrideNote', defaultMessage: 'Attributed to {selected} instead of {original}.' },
                  { selected: currentOption.full_name, original: assignedWorkerLabel },
                )}
              </p>
            )}
          </div>
        ) : null
        // KAN-1064: no static "Logged by {own name}" line — with a single option it
        // only repeated the signed-in person's own name.
      )}
      {isFollowUp && (
        <span style={pillIndigo}>
          {intl.formatMessage({ id: 'logJob.context.followUp', defaultMessage: 'Follow-up' })}
        </span>
      )}
      {isOffSeason && (
        <span style={pillAmber}>
          {intl.formatMessage({ id: 'logJob.context.offSeason', defaultMessage: 'Off-season' })}
        </span>
      )}
      {/* Pool type is standing context, not an exceptional flag — so it takes the
          neutral chip, not the uppercase amber/indigo pill treatment above. */}
      <PoolTypeTag poolType={customer?.pool_type} isPoolIndustry={isPoolIndustry} />
    </div>
  );
}

// Block-level flex (NOT inline-flex). The address element above is `display:
// inline-flex`; if this row were also inline-flex it'd run on the same line.
const rowStyle = { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, marginTop: 10 };
const bylineLabel = { fontSize: '0.75rem', color: fc.neutral.textMuted, fontWeight: fw.medium, fontFamily: foundation.fontFamily.sans };
const dropdownNameStyle = { fontSize: '0.75rem', color: fc.neutral.text, fontWeight: fw.semibold, fontFamily: foundation.fontFamily.sans };

// Subtle inline trigger — pill aesthetic, no heavy form-control look
const dropdownTriggerStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 7,
  padding: '4px 10px 4px 8px',
  borderRadius: 999,
  background: fc.neutral.bg,
  border: `1px solid ${fc.neutral.border}`,
  cursor: 'pointer',
  fontFamily: foundation.fontFamily.sans,
  lineHeight: 1.5,
  transition: 'background 120ms ease, border-color 120ms ease',
};

const selfTagStyle = {
  fontSize: '0.7rem',
  color: fc.neutral.textMuted,
  fontWeight: fw.normal,
};

const dropdownItem = {
  display: 'flex',
  alignItems: 'center',
  gap: sp[2],
  width: '100%',
  border: 'none',
  background: 'none',
  cursor: 'pointer',
  padding: `${sp[2]} ${sp[3]}`,
  borderRadius: rd.md,
  fontSize: fs.sm,
  fontFamily: foundation.fontFamily.sans,
  color: fc.neutral.text,
  textAlign: 'left',
};

const noteStyle = {
  margin: '4px 0 0',
  fontSize: '0.7rem',
  color: fc.neutral.textMuted,
  fontFamily: foundation.fontFamily.sans,
};

// Geometry unified to the canonical pill token (2026-08-06); the uppercase
// transform + wide letter-spacing are dropped with it — labels render as-is.
// Fill/text colors are this pill's own (indigo/amber) and stay off-token.
const pillBase = { ...components.statusBadge };
const pillIndigo = { ...pillBase, background: sem.tint.indigoPill, color: sem.role.admin };
const pillAmber = { ...pillBase, background: alphaOf(semantic.legacy.warning, 0.14), color: semantic.status.warningText };
